import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin, MenuOption, type MenuTextMatch } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';

import { Option } from '@shipengine/giger';
import { $createTagNode, Tag } from '../nodes/TagNode';
import { getMenuStyles } from './TagsPluginStyles';

// Regex used to match tags.
const AtSignRegexRegex = new RegExp('(^|\\s|\\()([@]((?:[^@_\\s](?:[_]|)){0,20}))$');

const resultsCache = new Map();

function useTagLookupService(tagString: string | null, tags: Tag[]) {
    const [results, setResults] = useState<Tag[]>([]);

    useEffect(() => {
        const cachedResults = resultsCache.get(tagString);

        if (tagString == null) {
            setResults([]);
            return;
        }

        if (cachedResults === null) {
            return;
        } else if (cachedResults !== undefined) {
            setResults(cachedResults);
            return;
        }

        resultsCache.set(tagString, null);

        setTimeout(() => {
            const newResults = tags.filter((tag) => tag.translation.toLowerCase().includes(tagString.toLowerCase()));

            resultsCache.set(tagString, newResults);
            setResults(newResults);
        }, 500);
    }, [tagString, tags]);

    return results;
}

function checkForTags(text: string, minMatchLength: number): MenuTextMatch | null {
    const match = AtSignRegexRegex.exec(text);

    if (match !== null) {
        // The strategy ignores leading whitespace but we need to know it's
        // length to add it to the leadOffset
        const maybeLeadingWhitespace = match[1];

        const matchingString = match[3];
        if (matchingString.length >= minMatchLength) {
            return {
                leadOffset: match.index + maybeLeadingWhitespace.length,
                matchingString,
                replaceableString: match[2],
            };
        }
    }
    return null;
}

class TagTypeaheadOption extends MenuOption {
    name: string;
    translation: string;

    constructor(tag: Tag) {
        super(tag.key);
        this.name = tag.key;
        this.translation = tag.translation;
    }
}

export function TagsPlugin({ tags }: { tags: Tag[] }) {
    const [editor] = useLexicalComposerContext();

    const [queryString, setQueryString] = useState<string | null>(null);

    const results = useTagLookupService(queryString, tags);

    const options = useMemo(() => results.map((result) => new TagTypeaheadOption(result)).slice(0, 5), [results]);

    const onSelectOption = useCallback(
        (selectedOption: TagTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
            editor.update(() => {
                const mentionNode = $createTagNode({
                    key: selectedOption.name,
                    translation: selectedOption.translation,
                });

                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode);
                }

                mentionNode.select();
                closeMenu();
            });
        },
        [editor],
    );

    const checkForTagMatch = useCallback((text: string) => {
        return checkForTags(text, 1);
    }, []);

    return (
        <LexicalTypeaheadMenuPlugin<TagTypeaheadOption>
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={checkForTagMatch}
            options={options}
            menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
                anchorElementRef.current && results.length
                    ? ReactDOM.createPortal(
                          <div css={getMenuStyles} role="menu">
                              {options.map((option, i: number) => (
                                  <Option
                                      key={option.key}
                                      value={option.key}
                                      focused={selectedIndex === i}
                                      onClick={() => {
                                          setHighlightedIndex(i);
                                          selectOptionAndCleanUp(option);
                                      }}
                                      onMouseEnter={() => {
                                          setHighlightedIndex(i);
                                      }}
                                  >
                                      {option.translation}
                                  </Option>
                              ))}
                          </div>,
                          anchorElementRef.current,
                      )
                    : null
            }
        />
    );
}
